import {ChangeDetectorRef, Component, EventEmitter, Input, Output} from '@angular/core';
import {FormControl} from "@angular/forms";
import {AuthService} from "../../_services/auth.service";
import {SnackbarService} from "../../_services/snackbar.service";
import {AdminService} from "../../_services/admin.service";
import {debounceTime, filter, of, switchMap, tap} from "rxjs";
import {catchError} from "rxjs/operators";
import {TimelineListItem} from "../../_types/memorybox";
import {User} from "../../_types/user";

@Component({
    selector: 'app-timeline-search',
    standalone: false,
    templateUrl: './timeline-search.component.html',
    styleUrl: './timeline-search.component.scss',
})
export class TimelineSearchComponent {
    @Input() title: string = 'Select Memory Box';
    @Input() placeholder: string = 'Select Memory Box';
    @Input() disabled: boolean = false;
    @Input() value: TimelineListItem | undefined;
    @Input() filter: Record<string, any> | undefined = undefined;
    @Output() timelineSelect: EventEmitter<TimelineListItem | undefined> = new EventEmitter();
    currentUser!: User;
    isRaa: boolean = false;
    timelineSearchControl: FormControl = new FormControl();
    loading: boolean = false;
    filteredTimelines: TimelineListItem[] = [];

    constructor(private authService: AuthService,
                private snackbarService: SnackbarService,
                private adminService: AdminService,
                private cdr: ChangeDetectorRef) {
        this.authService.getUser().subscribe({
            next: r => {
                this.currentUser = r;
            }
        })
        this.authService.getDynamicSettings().then((r): void => {
            this.isRaa = r.is_raa;
        });
        this.timelineSearchControl.valueChanges.pipe(
            debounceTime(300),
            filter((value): value is string => typeof value === 'string' && value.length > 2),
            tap((): boolean => this.loading = true),
            switchMap(value => {
                let filters: any = {
                    order_by: 'full_name',
                    direction: 'asc',
                    deactivated: false
                }
                if (this.currentUser.role.role_type !== 'admin') {
                    filters.access = 'write'
                }
                filters = Object.assign(filters, this.filter);

                return this.adminService.getTimelines(0, 100000, value, filters).pipe(
                    // Handle API errors gracefully without breaking the stream
                    catchError(e => {
                        this.snackbarService.openSnackBar(e, 'error');
                        return of({results: [] as TimelineListItem[]});
                    })
                );
            })
        ).subscribe(r => {
            this.loading = false;
            this.filteredTimelines = Array.from(
                new Map(r.results.map(timeline => [timeline.id, timeline])).values()
            );
            this.cdr.detectChanges();
        });

    }

    ngOnChanges(): void {
        if (this.value) {
            this.timelineSearchControl.setValue(this.value, {emitEvent: false});
        } else {
            this.timelineSearchControl.setValue(null, {emitEvent: false});
        }

        if (this.disabled) {
            this.timelineSearchControl.disable({emitEvent: false});
        } else {
            this.timelineSearchControl.enable({emitEvent: false});
        }
    }

    selectTimelineById(userId: number): void {
        const user = this.filteredTimelines.find(x => x.id === userId);

        if (!user) {
            return;
        }

        this.timelineSearchControl.setValue(user, {emitEvent: false});
        this.filteredTimelines = [];
        this.timelineSelect.emit(user);
    }

    displayFn = (value: TimelineListItem | number | null): string => {
        if (!value) {
            return '';
        }

        if (typeof value === 'number') {
            const user = this.filteredTimelines.find(x => x.id === value);
            return user
                ? this.authService.getDisplayName(user, !this.isRaa ? 'username' : undefined)
                : '';
        }

        return this.authService.getDisplayName(value, !this.isRaa ? 'username' : undefined);
    };

    nameDisplay(timeline: TimelineListItem): string {
        return this.authService.getDisplayName(timeline, this.isRaa ? 'reference_number' : undefined)
    }
}
